import { useMemo } from 'react';

import {
  GENERATION_TOOL,
  GENERATION_TOOL_GROUP,
  getSuggestToolsByGroup,
  getTeadsTools,
  getToolCopy,
  getToolTitle,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import { PROMPT_SUB_TEMPLATE_ID } from 'src/data/template';
import { isTeadsUI } from 'src/i18n';
import { useGenerationToolSettings } from 'src/services/useGenerationToolSettings';

import { usePrepareToolSubTemplatesInGenerationGroup } from './usePrepareToolSubTemplatesInGenerationGroup';
import { createBadgeTypeToolMapper } from './utils';

const TOOL_GROUPS = Object.values(GENERATION_TOOL_GROUP);

const appendTeads = (tools, shouldAppend) => {
  if (shouldAppend) {
    return [...tools, ...getTeadsTools()];
  }
  return tools;
};

export const mapGenerationTool = (generationTool) => ({
  tool: generationTool,
  title: getToolTitle(generationTool),
  description: getToolCopy(generationTool),
});

export const appendSubTemplates = (toolGroup, generationTool, prepareSubTemplates) => {
  const subTemplates = prepareSubTemplates(toolGroup, generationTool.tool);

  const prompt = {
    tool: generationTool.tool,
    id: PROMPT_SUB_TEMPLATE_ID,
    name: `${getToolTitle(generationTool.tool)} - Prompt`,
    description: getToolCopy(generationTool.tool),
  };

  return subTemplates ? [prompt, ...subTemplates] : generationTool;
};

export const useDocumentGroupedToolsWithoutRewritesAndBlog = (toolGroups = null) => {
  const prepareSubTemplates = usePrepareToolSubTemplatesInGenerationGroup();

  const teadsAvailable = isTeadsUI();
  const { newTools, improvedTools } = useGenerationToolSettings();

  const allTools = useMemo(() => {
    return TOOL_GROUPS.reduce((acc, toolGroup) => {
      let tools = getSuggestToolsByGroup(toolGroup);

      tools = tools.filter(
        (tool) => !isRewriteGenerationTool(tool) && !(tool === GENERATION_TOOL.blogDescription)
      );

      const shouldAppendTeads = teadsAvailable && toolGroup === GENERATION_TOOL_GROUP.ads;
      const toolsWithTeads = appendTeads(tools, shouldAppendTeads);
      const mappedTools = toolsWithTeads
        .map(mapGenerationTool)
        .flatMap((tool) => appendSubTemplates(toolGroup, tool, prepareSubTemplates))
        .map(createBadgeTypeToolMapper(newTools, improvedTools));

      return [...acc, { toolGroup, tools: mappedTools }];
    }, []);
  }, [teadsAvailable, newTools, improvedTools, prepareSubTemplates]);

  return useMemo(() => {
    if (Array.isArray(toolGroups) && toolGroups.length > 0) {
      return allTools.filter(({ toolGroup }) => toolGroups.includes(toolGroup));
    }

    return allTools;
  }, [allTools, toolGroups]);
};
